import React, { useCallback, useEffect, useState } from 'react';
import expedienteLaboralService from '../services/expedienteLaboralService';

const TIPOS_DOCUMENTO = [
  {
    tipo: 'dni',
    titulo: 'DNI',
    descripcion: 'Documento nacional de identidad del trabajador.',
  },
  {
    tipo: 'cv',
    titulo: 'CV',
    descripcion: 'Currículum vitae. Indica si está documentado.',
    requiereDocumentado: true,
  },
  {
    tipo: 'antecedentes_policiales',
    titulo: 'Antecedentes policiales',
    descripcion: 'Constancia con fecha de emisión. Vence a los 3 meses.',
    requiereFechaEmision: true,
  },
  {
    tipo: 'alta_plame',
    titulo: 'Alta en PLAME',
    descripcion: 'Constancia de alta del trabajador en PLAME.',
  },
  {
    tipo: 'derechohabientes',
    titulo: 'Derechohabientes',
    descripcion: 'Constancia de derechohabientes.',
  },
];

const formatearFecha = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const ExpedienteLaboralTab = ({ empleadoId }) => {
  const [loading, setLoading] = useState(true);
  const [guardandoTipo, setGuardandoTipo] = useState(null);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [documentos, setDocumentos] = useState({});
  const [alertaAntecedentes, setAlertaAntecedentes] = useState(null);
  const [historialAntecedentes, setHistorialAntecedentes] = useState([]);
  const [borradores, setBorradores] = useState({});

  const cargar = useCallback(async () => {
    if (!empleadoId) return;
    setLoading(true);
    setError('');
    const response = await expedienteLaboralService.obtenerPorEmpleado(empleadoId);
    if (response.success) {
      setDocumentos(response.data?.documentos || {});
      setAlertaAntecedentes(response.data?.alerta_antecedentes || null);
      setHistorialAntecedentes(response.data?.historial_antecedentes || []);
    } else {
      setError(response.error || 'No se pudo cargar el expediente');
    }
    setLoading(false);
  }, [empleadoId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const actualizarBorrador = (tipo, campo, valor) => {
    setBorradores((prev) => ({
      ...prev,
      [tipo]: {
        ...(prev[tipo] || {}),
        [campo]: valor,
      },
    }));
  };

  const subir = async (tipoConfig) => {
    const tipo = tipoConfig.tipo;
    const borrador = borradores[tipo] || {};
    if (!borrador.archivo) {
      setError(`Selecciona un archivo para ${tipoConfig.titulo}`);
      return;
    }
    if (tipoConfig.requiereFechaEmision && !borrador.fechaEmision) {
      setError('Ingresa la fecha de emisión de la constancia de antecedentes policiales');
      return;
    }

    setGuardandoTipo(tipo);
    setError('');
    setMensaje('');

    const response = await expedienteLaboralService.subirDocumento(empleadoId, {
      tipoDocumento: tipo,
      archivo: borrador.archivo,
      documentado: tipoConfig.requiereDocumentado ? Boolean(borrador.documentado) : undefined,
      fechaEmision: borrador.fechaEmision || undefined,
    });

    if (response.success) {
      setMensaje(response.message || 'Documento subido');
      setBorradores((prev) => ({ ...prev, [tipo]: {} }));
      await cargar();
    } else {
      setError(response.error || 'Error al subir el documento');
    }
    setGuardandoTipo(null);
  };

  const actualizarDocumentado = async (documento, documentado) => {
    setGuardandoTipo('cv');
    const response = await expedienteLaboralService.actualizarMetadatos(documento.id, { documentado });
    if (response.success) {
      await cargar();
    } else {
      setError(response.error || 'No se pudo actualizar el CV');
    }
    setGuardandoTipo(null);
  };

  const eliminar = async (documento) => {
    if (!window.confirm(`¿Eliminar ${documento.etiqueta || 'este documento'}?`)) return;
    setGuardandoTipo(documento.tipo_documento);
    const response = await expedienteLaboralService.eliminarDocumento(documento.id);
    if (response.success) {
      setMensaje('Documento eliminado');
      await cargar();
    } else {
      setError(response.error || 'No se pudo eliminar');
    }
    setGuardandoTipo(null);
  };

  if (!empleadoId) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-amber-800">
        Guarda el empleado primero para poder subir documentos del expediente laboral.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-600">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mr-3" />
        Cargando expediente laboral...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-gray-900">Expediente laboral</h3>
        <p className="text-sm text-gray-600 mt-1">
          Sube DNI, CV, antecedentes policiales, alta PLAME y constancia de derechohabientes.
          Formatos: PDF, JPG, PNG, DOC o DOCX (máx. 10 MB).
        </p>
      </div>

      {alertaAntecedentes && (
        <div
          className={`rounded-lg border p-4 ${
            alertaAntecedentes.nivel === 'error'
              ? 'bg-red-50 border-red-200 text-red-800'
              : 'bg-amber-50 border-amber-200 text-amber-900'
          }`}
        >
          <div className="font-semibold mb-1">
            {alertaAntecedentes.nivel === 'error' ? 'Antecedentes vencidos' : 'Antecedentes por vencer'}
          </div>
          <p className="text-sm">{alertaAntecedentes.mensaje}</p>
          <p className="text-xs mt-2 opacity-80">
            Vence: {formatearFecha(alertaAntecedentes.fecha_vencimiento)}
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
      )}
      {mensaje && (
        <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 text-sm">{mensaje}</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {TIPOS_DOCUMENTO.map((tipoConfig) => {
          const actual = documentos[tipoConfig.tipo];
          const borrador = borradores[tipoConfig.tipo] || {};
          const ocupado = guardandoTipo === tipoConfig.tipo;

          return (
            <div key={tipoConfig.tipo} className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h4 className="font-semibold text-gray-900">{tipoConfig.titulo}</h4>
                  <p className="text-xs text-gray-500 mt-1">{tipoConfig.descripcion}</p>
                </div>
                {actual && (
                  <span className="text-xs px-2 py-1 rounded-full bg-teal-50 text-teal-700 border border-teal-100">
                    Cargado
                  </span>
                )}
              </div>

              {actual ? (
                <div className="mb-4 rounded-md bg-gray-50 border border-gray-100 p-3 space-y-2">
                  <div className="text-sm text-gray-800 break-all">{actual.archivo_nombre}</div>
                  {tipoConfig.requiereFechaEmision && (
                    <div className="text-xs text-gray-600 space-y-1">
                      <div>Emisión: {formatearFecha(actual.fecha_emision)}</div>
                      <div>Vence: {formatearFecha(actual.fecha_vencimiento)}</div>
                      {actual.estado_vencimiento === 'vencido' && (
                        <div className="text-red-600 font-medium">Vencido — solicita una nueva constancia</div>
                      )}
                      {actual.estado_vencimiento === 'por_vencer' && (
                        <div className="text-amber-700 font-medium">Por vencer — solicita renovación</div>
                      )}
                    </div>
                  )}
                  {tipoConfig.requiereDocumentado && (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(actual.documentado)}
                        disabled={ocupado}
                        onChange={(e) => actualizarDocumentado(actual, e.target.checked)}
                      />
                      CV documentado
                    </label>
                  )}
                  <div className="flex flex-wrap gap-2 pt-1">
                    {actual.archivo_url && (
                      <a
                        href={actual.archivo_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs px-3 py-1.5 rounded bg-teal-600 text-white hover:bg-teal-700"
                      >
                        Ver / descargar
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => eliminar(actual)}
                      disabled={ocupado}
                      className="text-xs px-3 py-1.5 rounded border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mb-4 text-sm text-gray-500 italic">Aún no hay archivo cargado.</div>
              )}

              <div className="space-y-3 border-t border-gray-100 pt-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {actual ? 'Reemplazar archivo' : 'Subir archivo'}
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                    disabled={ocupado}
                    onChange={(e) => actualizarBorrador(tipoConfig.tipo, 'archivo', e.target.files?.[0] || null)}
                    className="block w-full text-sm text-gray-700"
                  />
                </div>

                {tipoConfig.requiereDocumentado && (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={Boolean(borrador.documentado)}
                      disabled={ocupado}
                      onChange={(e) => actualizarBorrador(tipoConfig.tipo, 'documentado', e.target.checked)}
                    />
                    Marcar CV como documentado
                  </label>
                )}

                {tipoConfig.requiereFechaEmision && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Fecha de emisión
                    </label>
                    <input
                      type="date"
                      value={borrador.fechaEmision || ''}
                      disabled={ocupado}
                      onChange={(e) => actualizarBorrador(tipoConfig.tipo, 'fechaEmision', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">El vencimiento se calculará a +3 meses.</p>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => subir(tipoConfig)}
                  disabled={ocupado || !borrador.archivo}
                  className="w-full px-3 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {ocupado ? 'Guardando...' : actual ? 'Subir nuevo archivo' : 'Subir documento'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {historialAntecedentes.length > 1 && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <h4 className="font-semibold text-gray-900 mb-3">Historial de antecedentes policiales</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Emisión</th>
                  <th className="py-2 pr-4">Vence</th>
                  <th className="py-2 pr-4">Estado</th>
                  <th className="py-2 pr-4">Archivo</th>
                  <th className="py-2">Vigente</th>
                </tr>
              </thead>
              <tbody>
                {historialAntecedentes.map((item) => (
                  <tr key={item.id} className="border-b border-gray-50">
                    <td className="py-2 pr-4">{formatearFecha(item.fecha_emision)}</td>
                    <td className="py-2 pr-4">{formatearFecha(item.fecha_vencimiento)}</td>
                    <td className="py-2 pr-4 capitalize">{item.estado_vencimiento || '—'}</td>
                    <td className="py-2 pr-4">
                      {item.archivo_url ? (
                        <a href={item.archivo_url} target="_blank" rel="noreferrer" className="text-teal-700 underline">
                          {item.archivo_nombre || 'Ver'}
                        </a>
                      ) : (
                        item.archivo_nombre || '—'
                      )}
                    </td>
                    <td className="py-2">{item.es_vigente ? 'Sí' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpedienteLaboralTab;
